import apiService from './ApiService';
import { GetDropdownDataByGroupApi, AxiosResponse } from '../utils/urlConst';

// Interfaces for dropdown data
export interface Species {
    name: string;
    wetenschappelijke_naam: string;
    sortering: string | null;
    species_id: number;
    species_subgroup_id: number;
}

export interface Trace {
    trace_id: number;
    description: string;
    species_subgroup_id: number | null;
    species_group_id: number;
}

export interface Behaviour {
    behaviour_id: number;
    description: string;
    species_group_id: number;
    species_subgroup_id: number | null;
}

export interface NdffStage {
    ndff_stage_id: number;
    description: string;
    species_group_id: number;
    species_subgroup_id: number | null;
}

export interface NdffCod {
    ndff_cod_id: number;
    description: string;
    species_group_id: number;
    species_subgroup_id: number | null;
}

export interface Residence {
    residence_id: number;
    description: string;
    species_group_id: number;
    species_subgroup_id: number | null;
}

export interface ResidenceLocation {
    residence_loc_id: number;
    description: string;
    species_group_id: number;
    species_subgroup_id: number | null;
}

export interface CountMethod {
    count_method_id: number;
    description: string;
}

export interface NdffGender {
    ndff_gender_id: number;
    description: string;
}

export interface SpeciesGroupDropdownData {
    species_group_id: number;
    name: string;
    species: Species[];
    traces: Trace[];
    behaviours: Behaviour[];
    ndff_stages: NdffStage[];
    ndff_cod: NdffCod[];
    residences: Residence[];
    residence_loc: ResidenceLocation[];
}

export interface DropdownDataResponse extends AxiosResponse {
    data: SpeciesGroupDropdownData[];
    count_methods: CountMethod[];
    ndff_gender: NdffGender[];
}

class DropdownApiService {
    private cachedDropdownData: DropdownDataResponse | null = null;
    private cacheTimestamp: number = 0;
    private readonly CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

    /**
     * Check if cached data is still valid
     * @returns boolean
     */
    private isCacheValid(): boolean {
        const now = Date.now();
        return this.cachedDropdownData !== null &&
            (now - this.cacheTimestamp) < this.CACHE_DURATION;
    }

    /**
     * Clear the cache
     */
    public clearCache(): void {
        this.cachedDropdownData = null;
        this.cacheTimestamp = 0;
    }
    /**
     * Fetch all dropdown data for all species groups with caching
     * @returns Promise<DropdownDataResponse | null>
     */
    public async getDropdownDataByGroup(): Promise<DropdownDataResponse | null> {
        try {
            // Check if we have valid cached data
            if (this.isCacheValid()) {
                return this.cachedDropdownData;
            }

            // If offline, return cached data even if expired, or null
            if (!navigator.onLine) {
                if (this.cachedDropdownData) {
                    return this.cachedDropdownData;
                } else {
                    return null;
                }
            }
            // Always fetch all groups without any parameters
            const response = await apiService.get<DropdownDataResponse>(GetDropdownDataByGroupApi);

            if (response && response.status && response.data) {
                // Cache the successful response
                this.cachedDropdownData = response;
                this.cacheTimestamp = Date.now();
                return response;
            }

            console.error('Failed to fetch dropdown data:', response?.status_message || 'Unknown error');
            return null;
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
            return null;
        }
    }

    /**
     * Map species_group_id to fauna species type
     * @param speciesGroupId - The species group ID
     * @param speciesSubgroupId - The species subgroup ID (optional)
     * @returns Object with faunaType and optional speciesGroupId for 'others'
     */
    public mapSpeciesGroupIdToFaunaType(speciesGroupId: number, speciesSubgroupId?: number): {
        faunaType: 'birds' | 'mammals' | 'bats' | 'others';
        speciesGroupId?: number;
    } {
        // Birds: species_group_id = 10
        if (speciesGroupId === 10) {
            return { faunaType: 'birds' };
        }

        // Mammals and Bats: species_group_id = 11
        if (speciesGroupId === 11) {
            // Bats: species_group_id = 11, species_subgroup_id = 30
            if (speciesSubgroupId === 30) {
                return { faunaType: 'bats' };
            }
            // Mammals: species_group_id = 11, exclude subgroup 30
            return { faunaType: 'mammals' };
        }

        // Others: species_group_id 1-9
        if (speciesGroupId >= 1 && speciesGroupId <= 9) {
            return { faunaType: 'others', speciesGroupId };
        }

        // Default fallback - treat as 'others'
        console.warn(`Unknown species_group_id: ${speciesGroupId}, treating as 'others'`);
        return { faunaType: 'others', speciesGroupId };
    }

    /**
     * Get filtered dropdown data based on species_group_id
     * @param speciesGroupId - The species group ID
     * @param speciesSubgroupId - The species subgroup ID (optional)
     * @returns Promise<Partial<DropdownDataResponse> | null>
     */
    public async getFilteredDropdownDataBySpeciesGroupId(
        speciesGroupId: number,
        speciesSubgroupId?: number
    ): Promise<Partial<DropdownDataResponse> | null> {
        const mapping = this.mapSpeciesGroupIdToFaunaType(speciesGroupId, speciesSubgroupId);

        return this.filterDropdownDataByFaunaSpecies(
            mapping.faunaType,
            mapping.speciesGroupId
        );
    }

    /**
     * Filter dropdown data based on fauna species selection
     * @param faunaSpeciesType - Type of fauna species ('birds', 'mammals', 'bats', 'others')
     * @param speciesGroupId - For 'others' type, specify which species_group_id (1-9)
     * @returns Promise<Partial<DropdownDataResponse> | null>
     */
    public async filterDropdownDataByFaunaSpecies(
        faunaSpeciesType: 'birds' | 'mammals' | 'bats' | 'others',
        speciesGroupId?: number
    ): Promise<Partial<DropdownDataResponse> | null> {
        try {
            const allData = await this.getDropdownDataByGroup();

            if (!allData || !allData.data) {
                return null;
            }

            const filteredData: SpeciesGroupDropdownData[] = [];

            // Filter species groups based on fauna species type
            allData.data.forEach(group => {
                const { species_group_id: groupId } = group;

                switch (faunaSpeciesType) {
                    case 'birds':
                        // Birds: species_group_id = 10
                        if (groupId === 10) {
                            filteredData.push(group);
                        }
                        break;

                    case 'mammals':
                        // Mammals: species_group_id = 11, exclude subgroup 30 (bats)
                        if (groupId === 11) {
                            const filteredGroup = { ...group };
                            // Filter out species with species_subgroup_id = 30 (bats)
                            if (filteredGroup.species) {
                                filteredGroup.species = filteredGroup.species.filter(
                                    species => species.species_subgroup_id !== 30
                                );
                            }
                            // Filter other arrays that might have subgroup 30
                            if (filteredGroup.behaviours) {
                                filteredGroup.behaviours = filteredGroup.behaviours.filter(
                                    behaviour => behaviour.species_subgroup_id !== 30
                                );
                            }
                            if (filteredGroup.ndff_stages) {
                                filteredGroup.ndff_stages = filteredGroup.ndff_stages.filter(
                                    stage => stage.species_subgroup_id !== 30
                                );
                            }
                            if (filteredGroup.traces) {
                                filteredGroup.traces = filteredGroup.traces.filter(
                                    trace => trace.species_subgroup_id !== 30
                                );
                            }
                            if (filteredGroup.residences) {
                                filteredGroup.residences = filteredGroup.residences.filter(
                                    residence => residence.species_subgroup_id !== 30
                                );
                            }
                            if (filteredGroup.residence_loc) {
                                filteredGroup.residence_loc = filteredGroup.residence_loc.filter(
                                    residenceLoc => residenceLoc.species_subgroup_id !== 30
                                );
                            }
                            if (filteredGroup.ndff_cod) {
                                filteredGroup.ndff_cod = filteredGroup.ndff_cod.filter(
                                    cod => cod.species_subgroup_id === null || cod.species_subgroup_id !== 30
                                );
                            }
                            filteredData.push(filteredGroup);
                        }
                        break;

                    case 'bats':
                        // Bats: species_group_id = 11, only subgroup 30
                        if (groupId === 11) {
                            const filteredGroup = { ...group };
                            // Only include species with species_subgroup_id = 30 (bats)
                            if (filteredGroup.species) {
                                filteredGroup.species = filteredGroup.species.filter(
                                    species => species.species_subgroup_id === 30
                                );
                            }
                            // Filter other arrays to only include subgroup 30
                            if (filteredGroup.behaviours) {
                                filteredGroup.behaviours = filteredGroup.behaviours.filter(
                                    behaviour => behaviour.species_subgroup_id === 30
                                );
                            }
                            if (filteredGroup.ndff_stages) {
                                filteredGroup.ndff_stages = filteredGroup.ndff_stages.filter(
                                    stage => stage.species_subgroup_id === 30
                                );
                            }
                            if (filteredGroup.traces) {
                                filteredGroup.traces = filteredGroup.traces.filter(
                                    trace => trace.species_subgroup_id === 30
                                );
                            }
                            if (filteredGroup.residences) {
                                filteredGroup.residences = filteredGroup.residences.filter(
                                    residence => residence.species_subgroup_id === 30
                                );
                            }
                            if (filteredGroup.residence_loc) {
                                filteredGroup.residence_loc = filteredGroup.residence_loc.filter(
                                    residenceLoc => residenceLoc.species_subgroup_id === 30
                                );
                            }
                            // Filter ndff_cod for bats (subgroup 30 only) - exclude general mammal entries with null
                            if (filteredGroup.ndff_cod) {
                                const originalCount = filteredGroup.ndff_cod.length;
                                const originalCods = filteredGroup.ndff_cod.map(cod => ({
                                    id: cod.ndff_cod_id,
                                    description: cod.description,
                                    subgroupId: cod.species_subgroup_id
                                }));

                                filteredGroup.ndff_cod = filteredGroup.ndff_cod.filter(
                                    cod => cod.species_subgroup_id === 30
                                );

                                const filteredCods = filteredGroup.ndff_cod.map(cod => ({
                                    id: cod.ndff_cod_id,
                                    description: cod.description,
                                    subgroupId: cod.species_subgroup_id
                                }));
                            }
                            filteredData.push(filteredGroup);
                        }
                        break;

                    case 'others':
                        // Others: species_group_id 1-9
                        if (groupId >= 1 && groupId <= 9) {
                            // If specific species group ID is provided, only include that one
                            if (speciesGroupId) {
                                if (groupId === speciesGroupId) {
                                    filteredData.push(group);
                                }
                            } else {
                                // Include all 'others' groups (1-9)
                                filteredData.push(group);
                            }
                        }
                        break;

                    default:
                        console.warn(`Unknown fauna species type: ${faunaSpeciesType}`);
                        break;
                }
            });

            const result = {
                data: filteredData,
                count_methods: allData.count_methods,
                ndff_gender: allData.ndff_gender,
                status: allData.status,
                status_message: allData.status_message
            };
            return result;

        } catch (error) {
            console.error('Error filtering dropdown data:', error);
            return null;
        }
    }
    public async getAllSpeciesGroups(): Promise<DropdownDataResponse | null> {
        return this.getDropdownDataByGroup(); // Call without parameters to get all groups
    }

    /**
     * Transform dropdown data into fauna species format for species selection
     * @returns Promise<FaunaSpecies[]>
     */
    public async getFaunaSpeciesGroups(): Promise<any[]> {
        try {
            const response = await this.getAllSpeciesGroups();
            if (!response?.data) {
                console.warn('No species groups data available - using fallback');
                return this.getFallbackFaunaSpeciesGroups();
            }

            // Initialize the 4 main fauna categories
            const faunaSpecies: any[] = [];
            const othersGroups: any[] = []; // Collect all "Others" groups (1-9)
            let hasBatsInGroup11 = false;
            let hasMammalsInGroup11 = false;
            let hasBirdsInGroup10 = false;

            // Process all groups and categorize them
            response.data.forEach(group => {
                const { species_group_id, name } = group;

                if (species_group_id === 10) {
                    hasBirdsInGroup10 = true;
                } else if (species_group_id === 11) {
                    // Check if this group has bats (subgroup 30)
                    const hasBats = group.species?.some((species: any) => species.species_subgroup_id === 30);
                    if (hasBats) {
                        hasBatsInGroup11 = true;
                    }
                    // Group 11 always has mammals (with or without bats)
                    hasMammalsInGroup11 = true;
                } else if (species_group_id >= 1 && species_group_id <= 9) {
                    // Collect all "Others" groups (1-9)
                    othersGroups.push({
                        species_group_id: species_group_id,
                        name: name
                    });
                }
            });

            // Create exactly 4 fauna species entries

            // 1. Birds (if group 10 exists)
            if (hasBirdsInGroup10) {
                faunaSpecies.push({
                    id: 'birds',
                    name: 'Vogels',
                    icon: 'birds',
                    species_group_id: 10
                });
            }

            // 2. Mammals (if group 11 exists)
            if (hasMammalsInGroup11) {
                faunaSpecies.push({
                    id: 'mammals',
                    name: 'Zoogdieren',
                    icon: 'mammals',
                    species_group_id: 11,
                    exclude_subgroups: hasBatsInGroup11 ? [30] : undefined
                });
            }

            // 3. Bats (if group 11 has subgroup 30)
            if (hasBatsInGroup11) {
                faunaSpecies.push({
                    id: 'bats',
                    name: 'Vleermuizen',
                    icon: 'bats',
                    species_group_id: 11,
                    species_subgroup_id: 30
                });
            }

            // 4. Others (if any groups 1-9 exist)
            if (othersGroups.length > 0) {
                faunaSpecies.push({
                    id: 'others',
                    name: 'Overig',
                    icon: 'others',
                    groups: othersGroups // Array of all species groups 1-9
                });
            }

            return faunaSpecies;

        } catch (error) {
            console.error('Error transforming fauna species groups:', error);
            return [];
        }
    }


    /**
     * Get fallback fauna species data for offline scenarios
     * @returns Array of 4 main fauna species categories
     */
    public getFallbackFaunaSpeciesGroups(): any[] {
        return [
            {
                id: 'birds',
                name: 'Vogels',
                icon: 'birds',
                species_group_id: 10,
            },
            {
                id: 'mammals',
                name: 'Zoogdieren',
                icon: 'mammals',
                species_group_id: 11,
            },
            {
                id: 'bats',
                name: 'Vleermuizen',
                icon: 'bats',
                species_group_id: 11,
                species_subgroup_id: 30,
            },
            {
                id: 'others',
                name: 'Overig',
                icon: 'others',
                groups: [
                    { species_group_id: 1, name: 'Amfibieën' },
                    { species_group_id: 2, name: 'Reptielen' },
                    { species_group_id: 3, name: 'Vissen' },
                    { species_group_id: 4, name: 'Insecten' },
                    { species_group_id: 5, name: 'Spinnen' },
                    { species_group_id: 6, name: 'Weekdieren' },
                    { species_group_id: 7, name: 'Kreeftachtigen' },
                    { species_group_id: 8, name: 'Planten' },
                    { species_group_id: 9, name: 'Overig' }
                ],
            },
        ];
    }
}

const dropdownApiService = new DropdownApiService();
export default dropdownApiService;